import "../../css/GlobalStyles.css";
import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import {
  Event,
  AccountData,
  SavedFormQuestion,
  VerificationState,
  FormAnswersV2,
  FollowSourceType,
  MediaAspectRatio,
} from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import { NameAndPhoneNumberInput } from "../../components/UserInfoContainers/NameAndPhoneNumberInput";
import { FormQuestionsInput } from "../../components/UserInfoContainers/FormQuestionsInput";
import {
  createEmptyFormAnswersV2,
  getPossessiveName,
  isEventExternalLink,
  normalizeUrlWithHttps,
  getThumbnailImageUrl,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import TopHeader from "../../components/TopHeader";
import { Colors } from "../../utils/colors";
import { API } from "../../API";
import { isDesktop } from "react-device-detect";
import { MixpanelContext } from "../../context/AnalyticsService";
import RectangleButton from "../../components/Buttons/RectangleButton";
import {
  getUsersFormResponses,
  validateEventForm,
  finalizeFormFileUploadAnswers,
} from "../../utils/eventUtils/formUtils";
import RecaptchaVerifierWrapper from "../../components/Wrappers/RecaptchaVerifierWrapper";
import {
  checkIfLoggedInPhoneNumber,
  checkIfUserIsUnsubscribed,
  checkIfUserIsUnsubscribedMarkit,
} from "../../utils/userUtils";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import EmptyStateButton from "../../components/Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { onProfilePageNavigatePath } from "../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import ProfilePic from "../../components/ProfilePic";
import InactiveProductState from "../../components/CreatorDashboard/Products/InactiveProductState";
import Footer from "../../components/Footer";
import ExpandableBox from "../../components/Containers/ExpandableBox";
import { HorizontalDivider } from "../../components/Dividers/HorizontalDivider";
import MediaCarouselContainer from "../../components/Media/MediaCarouselContainer";
import { FormResponsesProvider } from "../../components/Form/FormResponsesContext";
import { isPortraitAspectRatio } from "../../utils/mediaUtils";

type FullFormProps = {
  event: Event;
  host: AccountData;
  eventFormQuestions: SavedFormQuestion[];
};

// The screen that the viewer sees for the signup form
const FullForm = memo(function FullFormFn(props: FullFormProps) {
  const { event, host, eventFormQuestions } = props;
  const { theme } = useTheme(event.theme);
  const navigate = useNavigate();
  const { accountData, loggedIn, linkTrackerId } =
    useSelector(getAccountState).account;
  const mixpanel = useContext(MixpanelContext);

  const [verificationState, setVerificationState] = useState(
    VerificationState.UNVERIFIED
  );
  const [optedOut, setOptedOut] = useState(false);
  const [optedOutCreator, setOptedOutCreator] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [tempFullName, setTempFullName] = useState("");
  const [guestUid, setGuestUid] = useState("");
  const [processing, setProcessing] = useState(false);
  const [answers, setAnswers] = useState<FormAnswersV2>(
    createEmptyFormAnswersV2(eventFormQuestions, [])
  );
  const [submittedNonRedirectForm, setSubmittedNonRedirectForm] =
    useState(false);
  const [formErrorIds, setFormErrorIds] = useState<string[]>([]);
  const [formFileUploads, setFormFileUploads] = useState<
    Record<string, string>
  >({});
  const [loadingFilledResponses, setLoadingFilledResponses] = useState(false);

  const portraitAspectRatio = useMemo(
    () => isPortraitAspectRatio(event.mediaAspectRatio),
    [event.mediaAspectRatio]
  );

  const styles = {
    alternateContainer: {
      margin: isDesktop ? "auto" : undefined,
      paddingTop: isDesktop ? undefined : 160,
      width: isDesktop ? 293 : undefined,
    },
    mediaFrame: {
      width: "100%",
      maxWidth: portraitAspectRatio ? 262 : 362,
      aspectRatio: event.mediaAspectRatio || MediaAspectRatio._1_1,
      borderRadius: 20,
      border: `1px solid ${theme.DividerColor.borderColor}`,
      overflow: "hidden",
      position: "relative" as const,
      flexShrink: 0,
    },
  };

  const isEmailBasedAccount = useMemo(
    () => accountData.email !== "",
    [accountData.email]
  );

  const isDisabledInputSection = useMemo(
    () =>
      verificationState !== VerificationState.VERIFIED ||
      processing ||
      isEmailBasedAccount ||
      optedOut ||
      optedOutCreator,
    [
      isEmailBasedAccount,
      optedOut,
      optedOutCreator,
      processing,
      verificationState,
    ]
  );

  // NOTE: For the useEffects, these are the same as in FullEventInputForm. Can consider making a hook to put the logic in there and call in both files

  // set existing user and follower data if you're logged in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (checkIfLoggedInPhoneNumber(loggedIn, accountData.phoneNumber)) {
      setTempFullName(accountData.fullName);
      setTempPhoneNumber(accountData.phoneNumber);
      setGuestUid(accountData.uid);
      setVerificationState(VerificationState.VERIFIED);

      setLoadingFilledResponses(true);
      // Check if logged in phone number is unsubscribed from markit number or creator
      // Even if using firebase OTP, we need them opted in so they can get confirmation texts
      const isUnsubscribedMarkit = await checkIfUserIsUnsubscribedMarkit(
        accountData.phoneNumber
      );
      setOptedOut(isUnsubscribedMarkit);

      const isUnsubscribed = await checkIfUserIsUnsubscribed(
        accountData.uid,
        event.createdBy
      );
      setOptedOutCreator(isUnsubscribed);

      const { formResponses, fileNameRecords } = await getUsersFormResponses(
        event.createdBy,
        eventFormQuestions,
        accountData.uid
      );
      setAnswers(createEmptyFormAnswersV2(eventFormQuestions, formResponses));
      setFormFileUploads(fileNameRecords);
      setLoadingFilledResponses(false);
    }
  }, [
    accountData.fullName,
    accountData.phoneNumber,
    accountData.uid,
    event,
    eventFormQuestions,
    loggedIn,
    tempPhoneNumber,
  ]);

  const redirectToExternalLink = useCallback(() => {
    setTimeout(() => {
      // Open the redirect in a new tab instead of replacing the form window, so
      // the form window stays put and the user can navigate back.
      window.open(normalizeUrlWithHttps(event.externalLink), "_blank");
      if (mixpanel) {
        mixpanel.track("Webapp: Redirected To External Event Link", {
          event_id: event.id,
          external_link: event.externalLink,
          event_type: event.eventType,
          phone_number: tempPhoneNumber,
          already_submitted: false,
        });
      }
    }, 200);
  }, [
    event.eventType,
    event.externalLink,
    event.id,
    mixpanel,
    tempPhoneNumber,
  ]);

  const rsvpToExternalEvent = useCallback(async () => {
    // prevent host from getting ticket
    if (event.createdBy === guestUid) {
      alert("You are the host of this event and cannot get a ticket.");
      return;
    }
    setProcessing(true);

    // Check if required form questions are answered
    const errorIds = validateEventForm(answers, eventFormQuestions);
    if (errorIds.length > 0) {
      setFormErrorIds(errorIds);
      alert("Please answer the required questions.");
      setProcessing(false);
      return;
    }

    if (mixpanel) {
      mixpanel.track("Webapp: Attempting RSVP To Event", {
        event_id: event.id,
        customer_phone: tempPhoneNumber,
        form_responses: answers,
        event_type: event.eventType,
      });
    }

    if (mixpanel) {
      mixpanel.track("Webapp: Calling Enqueue RSVP To Event", {
        event_id: event.id,
        customer_phone: tempPhoneNumber,
        form_responses: answers,
        event_type: event.eventType,
      });
    }

    let answersToSubmit: FormAnswersV2 = answers;
    if (eventFormQuestions.length > 0) {
      try {
        answersToSubmit = await finalizeFormFileUploadAnswers({
          creatorUid: event.createdBy,
          answers,
          formQuestions: eventFormQuestions,
          fileNamesByQuestionId: formFileUploads,
          setAnswers,
          eventId: event.id,
          followerUid: guestUid,
        });
      } catch {
        setProcessing(false);
        alert("Failed to upload a file. Please try again.");
        return;
      }
    }

    await API.rsvp
      .enqueueRSVPToEvent({
        toPhoneNumber: tempPhoneNumber,
        fullName: tempFullName,
        eventId: event.id,
        followSourceType: isDesktop
          ? FollowSourceType.DESKTOP_WEB
          : FollowSourceType.MOBILE_WEB,
        answers: eventFormQuestions.length !== 0 ? answersToSubmit : undefined,
        customTicketIds: [event.customTickets[0].id],
        numberOfTicketsByCustomTicketId: {
          [event.customTickets[0].id]: 1,
        },
        redeemTicketIds: [],
        promoCodeId: "",
        promoterId: "",
        linkId: linkTrackerId,
        userReferrer: "",
        amount: 0,
        chargeId: "",
        customerId: "",
        uid: "",
      })
      .then(async (response) => {
        const { success } = response;
        if (!success) {
          if (mixpanel) {
            mixpanel.track("Webapp: Failed RSVP To Event", {
              event_id: event.id,
              customer_phone: tempPhoneNumber,
              error_message: "success returned false",
              form_responses: answersToSubmit,
              event_type: event.eventType,
            });
          }
        }
      })
      .catch((e: any) => {
        if (mixpanel) {
          mixpanel.track("Webapp: Failed RSVP To Event", {
            event_id: event.id,
            customer_phone: tempPhoneNumber,
            error_message: e.message,
            form_responses: answersToSubmit,
            event_type: event.eventType,
          });
        }
      })
      .finally(() => {
        // Always show the submitted UI on the form window so the user keeps the
        // window and can press back; additionally open the external redirect (if
        // any) in a new tab.
        setSubmittedNonRedirectForm(true);
        if (event.externalLink) {
          redirectToExternalLink();
        }
      });
  }, [
    event,
    guestUid,
    answers,
    eventFormQuestions,
    mixpanel,
    tempPhoneNumber,
    tempFullName,
    formFileUploads,
    linkTrackerId,
    redirectToExternalLink,
  ]);

  const renderVisitProfileButton = useMemo(
    () => (
      <div style={{ paddingInline: isDesktop ? 0 : 24 }}>
        <RectangleButton
          buttonLabel={
            <span className="bodySmallMedium" style={{ fontWeight: 600 }}>
              Visit {getPossessiveName(host.fullName)} profile
            </span>
          }
          onPress={() => onProfilePageNavigatePath(navigate, host.username)}
          theme={theme}
          altBorderRadius={100}
          altColor={Colors.BLACK}
          altTextColor={theme.ButtonText.color}
          iconLeft={
            <div>
              <ProfilePic user={host} size={31} />
            </div>
          }
          altPaddingHorz={7}
          containerStyles={{ height: 44, width: "auto", margin: "auto" }}
          iconRight={
            <div>
              <Icon
                icon="tabler:arrow-up-right"
                height={15}
                style={{ marginBottom: -3, ...theme.ButtonText }}
              />
            </div>
          }
        />
      </div>
    ),
    [host, navigate, theme]
  );

  const renderInactiveForm = useMemo(
    () => (
      <div className="ColumnCentering" style={styles.alternateContainer}>
        <InactiveProductState
          eventType={event.eventType}
          host={host}
          theme={theme}
        />
      </div>
    ),
    [event.eventType, host, styles.alternateContainer, theme]
  );

  const renderNoRedirectForm = useMemo(
    () => (
      <div className="ColumnCentering" style={styles.alternateContainer}>
        <EmptyStateButton
          title="Thank you for completing this form!"
          description={`${host.fullName} uses Markit to send updates, reminders, and more.`}
          icon={
            <Icon
              icon={"ion:checkmark-circle"}
              height={60}
              style={{ color: Colors.WHITE }}
            />
          }
          iconBackgroundColor={Colors.GREEN2}
          iconBox={70}
          theme={theme}
        />
        {renderVisitProfileButton}
      </div>
    ),
    [host.fullName, renderVisitProfileButton, styles.alternateContainer, theme]
  );

  return (
    <RecaptchaVerifierWrapper>
      <div className="ColumnNormalSpaced" style={{ minHeight: "100vh" }}>
        <div className="ColumnNormal" style={{ flex: 1 }}>
          <TopHeader eventTheme={event.theme} />
          {event.isManualSoldOut ? (
            renderInactiveForm
          ) : submittedNonRedirectForm ? (
            renderNoRedirectForm
          ) : (
            <div
              className="ColumnNormal"
              style={{
                paddingTop: 20,
                paddingBottom: isDesktop ? 60 : 30,
                alignSelf: "center",
                width: isDesktop ? 362 : "100%",
                gap: 24,
              }}
            >
              {getThumbnailImageUrl(event) ? (
                <div className="AlignedColumn" style={{ paddingInline: 14 }}>
                  <div style={styles.mediaFrame}>
                    <MediaCarouselContainer
                      event={event}
                      selectedMediaIndex={0}
                      setSelectedMediaIndex={() => {}}
                    />
                  </div>
                </div>
              ) : null}
              <div
                className="ColumnNormal"
                style={{ paddingInline: isDesktop ? 0 : 14, gap: 20 }}
              >
                <NameAndPhoneNumberInput
                  host={host}
                  verificationState={verificationState}
                  setVerificationState={setVerificationState}
                  optedOut={optedOut}
                  setOptedOut={setOptedOut}
                  optedOutCreator={optedOutCreator}
                  setOptedOutCreator={setOptedOutCreator}
                  tempFullName={tempFullName}
                  setTempFullName={setTempFullName}
                  tempPhoneNumber={tempPhoneNumber}
                  setTempPhoneNumber={setTempPhoneNumber}
                  setUid={setGuestUid}
                  processing={processing}
                  event={event}
                  callbackOnVerify={
                    event &&
                    isEventExternalLink(event.eventType) &&
                    eventFormQuestions.length === 0
                      ? rsvpToExternalEvent
                      : undefined
                  }
                />
                {eventFormQuestions.length > 0 ? (
                  <>
                    <HorizontalDivider altMargin={0} eventTheme={event.theme} />
                    <ExpandableBox>
                      <div className="ColumnNormal" style={{ gap: 20 }}>
                        <FormResponsesProvider
                          formFileUploads={formFileUploads}
                          setFormFileUploads={setFormFileUploads}
                          formErrorIds={formErrorIds}
                          setFormErrorIds={setFormErrorIds}
                        >
                          <FormQuestionsInput
                            formQuestions={eventFormQuestions}
                            answers={answers}
                            setAnswers={setAnswers}
                            disabled={isDisabledInputSection}
                            loading={loadingFilledResponses}
                            showQuestionsPreview
                            verified={
                              verificationState === VerificationState.VERIFIED
                            }
                          />
                        </FormResponsesProvider>
                        <RectangleButton
                          buttonLabel={<span>Submit</span>}
                          onPress={rsvpToExternalEvent}
                          theme={theme}
                          altPaddingVert={12}
                          loading={processing}
                          disabled={isDisabledInputSection}
                        />
                      </div>
                    </ExpandableBox>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </div>
        <Footer creatorData={host} />
      </div>
    </RecaptchaVerifierWrapper>
  );
});

export default FullForm;
